import {
  type LessonSummary,
  type SessionDelivery,
  type SessionSummary,
} from '@/platform/learning/contract'

import { useFocusOnMount } from './focus'
import { MockBadge } from './MockBadge'
import {
  MOCK_RESULT_NOTE,
  OBJECTIVE_STATUS_TEXT,
  PRACTICE_HEADING,
  SAMPLE_CONTENT_RESULT_NOTE,
} from './result-text'

/**
 * Learning Result (UI v0.2 §4.5, first-learning-journey §4 and §6.2): only
 * facts from the session summary — how many items were done, which were
 * correct with their explanations, the backend's per-attempt objective
 * observations and its supplementary-practice suggestions. Nothing is
 * recalculated here, and there are no mastery, level or progress claims
 * (FR-145).
 */

export interface SessionResultProps {
  readonly summary: SessionSummary
  /** The session the summary belongs to: question order and content label. */
  readonly delivery: SessionDelivery
  readonly onHome: () => void
  readonly onStartPractice: (lesson: LessonSummary) => void
  /** A practice lesson is being opened. */
  readonly starting: boolean
  readonly startFailed: boolean
}

export function SessionResult({
  summary,
  delivery,
  onHome,
  onStartPractice,
  starting,
  startFailed,
}: SessionResultProps): React.JSX.Element {
  const heading = useFocusOnMount<HTMLHeadingElement>()
  const note = summary.is_mock
    ? MOCK_RESULT_NOTE
    : delivery.is_sample_content
      ? SAMPLE_CONTENT_RESULT_NOTE
      : null
  const questionNumber = (itemId: string) =>
    delivery.items.findIndex((i) => i.item_id === itemId) + 1

  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center gap-5 px-5 pt-6 pb-16 text-center">
      <p className="text-6xl" aria-hidden>
        🏁
      </p>
      <h1 ref={heading} tabIndex={-1} className="text-3xl font-bold">
        Con đã xong bài học!
      </h1>
      {summary.is_mock && <MockBadge />}
      <dl className="flex w-full flex-col gap-3 rounded-3xl bg-white p-5 text-lg dark:bg-slate-900">
        <div className="flex justify-between gap-4">
          <dt>Số câu đã làm</dt>
          <dd className="font-bold">
            {summary.items_submitted}/{summary.items_total}
          </dd>
        </div>
        {summary.graded !== null && (
          <div className="flex justify-between gap-4">
            <dt>Số câu đúng</dt>
            <dd className="font-bold">
              {summary.graded.correct}/{summary.graded.total}
            </dd>
          </div>
        )}
      </dl>
      {note !== null && (
        <p className="text-base text-slate-600 dark:text-slate-400">{note}</p>
      )}

      {summary.results.some((r) => r.outcome === 'graded') && (
        <section aria-labelledby="result-items" className="flex w-full flex-col gap-3">
          <h2 id="result-items" className="text-xl font-bold">
            Kết quả từng câu
          </h2>
          <ul className="flex flex-col gap-2 text-left">
            {summary.results.flatMap((r) =>
              r.outcome === 'graded'
                ? [
                    <li
                      key={r.item_id}
                      className="rounded-2xl bg-white p-4 wrap-break-word dark:bg-slate-900"
                    >
                      <p className="text-lg font-semibold">
                        Câu {questionNumber(r.item_id)}:{' '}
                        {r.is_correct ? 'Đúng' : 'Chưa đúng'}
                      </p>
                      {r.feedback.correct_answer_text !== null && (
                        <p className="text-base">
                          Đáp án đúng:{' '}
                          <span lang="en" className="font-semibold">
                            {r.feedback.correct_answer_text}
                          </span>
                        </p>
                      )}
                      {r.feedback.explanation_vi !== null && (
                        <p className="text-base text-slate-700 dark:text-slate-300">
                          {r.feedback.explanation_vi}
                        </p>
                      )}
                    </li>,
                  ]
                : [],
            )}
          </ul>
        </section>
      )}

      {summary.objectives.length > 0 && (
        <section
          aria-labelledby="result-objectives"
          className="flex w-full flex-col gap-3"
        >
          <h2 id="result-objectives" className="text-xl font-bold">
            Theo mục tiêu học
          </h2>
          <ul className="flex flex-col gap-2 text-left">
            {summary.objectives.map((o) => (
              <li
                key={o.code}
                className="rounded-2xl bg-white p-4 wrap-break-word dark:bg-slate-900"
              >
                <p className="text-lg font-semibold">{o.label_vi}</p>
                <p className="text-base">{OBJECTIVE_STATUS_TEXT[o.status]}</p>
                {o.attempted > 0 && (
                  <p className="text-base text-slate-700 dark:text-slate-300">
                    Đúng {o.correct}/{o.attempted} câu
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {summary.recommended_practice.length > 0 && (
        <section aria-labelledby="result-practice" className="flex w-full flex-col gap-3">
          <h2 id="result-practice" className="text-xl font-bold">
            {PRACTICE_HEADING}
          </h2>
          {startFailed && (
            <p role="alert" className="text-base text-rose-700 dark:text-rose-400">
              Chưa mở được bài luyện thêm. Con thử lại nhé.
            </p>
          )}
          {summary.recommended_practice.map((lesson) => (
            <button
              key={lesson.lesson_id}
              type="button"
              disabled={starting}
              className="h-14 w-full rounded-2xl border-2 border-sky-700 text-lg font-semibold text-sky-800 disabled:opacity-40 dark:border-sky-400 dark:text-sky-300"
              onClick={() => {
                onStartPractice(lesson)
              }}
            >
              {lesson.title}
            </button>
          ))}
        </section>
      )}

      <button
        type="button"
        className="h-14 w-full rounded-2xl bg-sky-700 text-lg font-semibold text-white active:bg-sky-800"
        onClick={onHome}
      >
        Về trang chính
      </button>
    </div>
  )
}
